import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';

function dateIsFuture(value) {
    if (!value) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) && date > new Date();
}

function reconstructUrl(currentUrl, slug) {
    if (!currentUrl || !slug) return currentUrl;
    try {
        const url = new URL(currentUrl);
        const hostParts = url.hostname.split('.').filter(Boolean);
        url.hostname = hostParts.length > 1
            ? [slug, ...hostParts.slice(1)].join('.')
            : `${slug}.${url.hostname}`;
        url.pathname = '/';
        url.search = '';
        url.hash = '';
        return url.toString();
    } catch {
        return currentUrl.replace(/^(https?:\/\/)?([^/:]+)(:\d+)?(.*)$/i, (match, protocol = '', hostname, port = '') => {
            const hostParts = hostname.split('.').filter(Boolean);
            const nextHostname = hostParts.length > 1
                ? [slug, ...hostParts.slice(1)].join('.')
                : `${slug}.${hostname}`;
            return `${protocol}${nextHostname}${port || ''}/`;
        });
    }
}

export function useStorefrontSettings() {
    const { fetch, API_URL } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await fetch(`${API_URL}/v1/seller/storefront`, {
                headers: { Accept: 'application/json' },
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to load storefront settings');
            setData({
                ...json.data,
                storefrontLayoutOptions: json.meta?.storefrontLayoutOptions || null,
            });
        } catch (error) {
            setLoadError(error.message || 'Failed to load storefront settings');
        } finally {
            setLoading(false);
        }
    }, [fetch, API_URL]);

    useEffect(() => {
        reload();
    }, [reload]);

    const request = useCallback(async (url, options = {}) => {
        try {
            const res = await fetch(`${API_URL}${url}`, {
                ...options,
                headers: {
                    Accept: 'application/json',
                    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
                    ...(options.headers || {}),
                },
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) return { success: false, error: json.error || 'Save failed' };
            return { success: true, data: json.data || {} };
        } catch (error) {
            return { success: false, error: error.message || 'Save failed' };
        }
    }, [fetch, API_URL]);

    const saveAndMerge = useCallback(async (url, method, payload) => {
        const result = await request(url, {
            method,
            body: typeof payload === 'undefined' ? undefined : JSON.stringify(payload),
        });
        if (result.success) {
            setData(prev => ({ ...(prev || {}), ...(result.data || {}) }));
        }
        return result;
    }, [request]);

    const saveSlug = useCallback(async (slug) => {
        const result = await request('/v1/seller/branding/url', {
            method: 'POST',
            body: JSON.stringify({ slug }),
        });
        if (result.success) {
            setData(prev => ({
                ...(prev || {}),
                slug: result.data?.slug || slug,
                url: reconstructUrl(prev?.url, result.data?.slug || slug),
            }));
        }
        return result;
    }, [request]);

    const checkSlugAvailability = useCallback(async (slug) => {
        const cleanSlug = String(slug || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
        if (!cleanSlug) {
            return { success: true, available: false, slug: cleanSlug, error: 'Enter a URL slug.' };
        }
        const result = await request(`/v1/seller/storefront/slug/${encodeURIComponent(cleanSlug)}`, {
            method: 'GET',
        });
        if (!result.success) return result;
        return {
            success: true,
            available: !!result.data?.available,
            slug: result.data?.slug || cleanSlug,
            error: result.data?.error || null,
            reason: result.data?.reason || null,
        };
    }, [request]);

    const saveBranding = useCallback(async (payload) => {
        const result = await request('/v1/seller/branding/branding', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        if (result.success) {
            setData(prev => ({
                ...(prev || {}),
                branding: result.data?.branding || prev?.branding || {},
                storefrontLayout: result.data?.storefrontLayout || prev?.storefrontLayout || {},
                contrastWarnings: result.data?.contrastWarnings || [],
            }));
        }
        return result;
    }, [request]);

    const setTranslations = useCallback((translations) => {
        setData(prev => (prev ? { ...prev, translations } : prev));
    }, []);

    const resetBranding = useCallback(async () => {
        const result = await request('/v1/seller/branding/resetbranding', { method: 'POST' });
        if (result.success) await reload();
        return result;
    }, [request, reload]);

    const saveVacation = useCallback(async (vacationEndsAt) => {
        const result = await request('/v1/seller/storefront/vacation', {
            method: 'POST',
            body: JSON.stringify({ vacationEndsAt: vacationEndsAt || '' }),
        });
        if (result.success) {
            setData(prev => ({
                ...(prev || {}),
                vacationEndsAt: result.data?.vacationEndsAt || null,
                isOnVacation: dateIsFuture(result.data?.vacationEndsAt),
            }));
        }
        return result;
    }, [request]);

    return {
        data,
        loading,
        loadError,
        reload,
        checkSlugAvailability,
        saveSlug,
        saveBasics: (payload) => saveAndMerge('/v1/seller/branding/basics', 'POST', payload),
        saveImagery: (payload) => saveAndMerge('/v1/seller/storefront/imagery', 'PUT', payload),
        saveLocation: (payload) => saveAndMerge('/v1/seller/storefront/location', 'PUT', payload),
        removeLocation: async () => {
            const result = await request('/v1/seller/storefront/location', { method: 'DELETE' });
            if (result.success) {
                setData(prev => ({
                    ...(prev || {}),
                    location: null,
                    basics: prev?.basics ? { ...prev.basics, location: null } : prev?.basics,
                }));
            }
            return result;
        },
        saveBranding,
        saveInternationalization: (payload) => saveAndMerge(
            '/v1/seller/storefront/internationalization',
            'PUT',
            payload
        ),
        resetBranding,
        saveTextCopy: (payload) => saveAndMerge('/v1/seller/branding/textcopy', 'POST', payload),
        saveVacation,
        setTranslations,
    };
}
